import React, { useState, useEffect, useMemo } from "react";
import { UserDashboardHeader, StudentNavbar, SideNavigation } from "../components";
import { BsArrowLeftCircle, BsArrowRightCircle } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import axios from "axios";

import bio from "../assets/biology.avif";
import lit from "../assets/literature.jpeg";
import hist from "../assets/history.jpeg";
import phys from "../assets/physics.jpeg";
import rel from "../assets/religion.jpeg";
import eng from "../assets/english.png";
import chem from "../assets/chemistry.avif";
import com from "../assets/commerce.jpeg";
import geo from "../assets/geography.jpeg";
import math from "../assets/math.webp";
import acc from "../assets/accounting.jpeg";

const UserDashboard = () => {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [currentActiveIndex, setCurrentActiveIndex] = useState(0);
  const [currentPastIndex, setCurrentPastIndex] = useState(0);

  const [activeEnrollments, setActiveEnrollments] = useState([]);
  const [pastEnrollmentsFromDatabase, setPastEnrollments] = useState([]);

  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

  const allSubjects = useMemo(
    () => ({
      Biology: bio,
      Physics: phys,
      Chemistry: chem,
      Religion: rel,
      Accounting: acc,
      History: hist,
      Literature: lit,
      Geography: geo,
      Mathematics: math,
      Commerce: com,
      "English Language": eng,
    }),
    []
  );

  const examSlug = "bgcse";
  const slugify = (s = "") =>
    String(s)
      .trim()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");

  const numberOfActiveEnrollments = activeEnrollments.length;
  const numberOfPastEnrollments = pastEnrollmentsFromDatabase.length;

  const getEnrollments = async () => {
    try {
      const [enrollmentsRes, subjectsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/enrollments`),
        axios.get(`${API_BASE_URL}/subjects`),
      ]);

      const enrollments = enrollmentsRes.data || [];
      const subjects = subjectsRes.data || [];

      const subjectMap = subjects.reduce((map, subject) => {
        map[subject.id] = subject.subject_name;
        return map;
      }, {});

      const active = enrollments
        .filter(
          (enrollment) =>
            enrollment.progress !== 100 && enrollment.student_id === storedUser.id
        )
        .map((enrollment) => subjectMap[enrollment.subject_id])
        .filter(Boolean);

      const past = enrollments
        .filter(
          (enrollment) =>
            enrollment.progress === 100 && enrollment.student_id === storedUser.id
        )
        .map((enrollment) => subjectMap[enrollment.subject_id])
        .filter(Boolean);

      setActiveEnrollments(active);
      setPastEnrollments(past);

      // reset indices if lists shrink
      setCurrentActiveIndex(0);
      setCurrentPastIndex(0);
    } catch (error) {
      setActiveEnrollments([]);
      setPastEnrollments([]);
      setCurrentActiveIndex(0);
      setCurrentPastIndex(0);
    }
  };

  useEffect(() => {
    getEnrollments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateToSubjectPage = ({ subject }) => {
    const subjectSlug = slugify(subject);
    navigate(`/${examSlug}/${subjectSlug}`);
  };

  // Helpers to “window” up to 4 subjects, cycling like your original code
  const calculateActiveSubjects = () => {
    const toShow = [];
    if (!numberOfActiveEnrollments) return toShow;

    for (let i = 0; i < Math.min(4, numberOfActiveEnrollments); i++) {
      const index = (currentActiveIndex + i) % numberOfActiveEnrollments;
      toShow.push(activeEnrollments[index]);
    }
    return toShow;
  };

  const calculatePastSubjects = () => {
    const toShow = [];
    if (!numberOfPastEnrollments) return toShow;

    for (let i = 0; i < Math.min(4, numberOfPastEnrollments); i++) {
      const index = (currentPastIndex + i) % numberOfPastEnrollments;
      toShow.push(pastEnrollmentsFromDatabase[index]);
    }
    return toShow;
  };

  const handleActiveNext = () => {
    if (!numberOfActiveEnrollments) return;
    setCurrentActiveIndex((prev) => (prev + 1) % numberOfActiveEnrollments);
  };

  const handleActivePrevious = () => {
    if (!numberOfActiveEnrollments) return;
    setCurrentActiveIndex(
      (prev) => (prev - 1 + numberOfActiveEnrollments) % numberOfActiveEnrollments
    );
  };

  // Keep your “past” logic basically the same (only scroll if enough items)
  const handlePastNext = () => {
    if (currentPastIndex < pastEnrollmentsFromDatabase.length - 5) {
      setCurrentPastIndex((prev) => prev + 1);
    }
  };

  const handlePastPrevious = () => {
    if (currentPastIndex > 0) {
      setCurrentPastIndex((prev) => prev - 1);
    }
  };

  return (
    <div className="min-h-screen w-full">
      {/* If your StudentNavbar is fixed, leave it fixed but add top padding below */}
      <StudentNavbar className="fixed top-0 w-full z-50" />

      {/* Push content below fixed navbar */}
      <div >
        <UserDashboardHeader />

        {/* Page padding */}
        <div className="px-3 sm:px-6 py-6">
          {/* Mobile: open sidebar button */}
          <div className="lg:hidden mb-4 w-full">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="inline-flex w-full items-center gap-2 rounded-lg bg-white/10 border border-white/10 px-4 py-2 text-sm text-white hover:bg-white/15 transition"
            >
              <span className="text-lg">☰</span>
               Menu
            </button>
          </div>

          {/* 2-column layout on desktop; single column on mobile */}
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
            {/* Desktop sidebar */}
            <aside className="hidden lg:block">
              <SideNavigation />
            </aside>

            {/* Main content */}
            <main className="w-full lg:border-l lg:border-white/10 lg:pl-6">
              {/* ===================== ACTIVE SUBJECTS ===================== */}
              <div className="flex items-center justify-between gap-3">
                <p className="text-white font-thin text-3xl sm:text-4xl 2xl:text-5xl py-4 sm:py-6">
                  Active Subjects
                </p>
              </div>

              <div className="flex items-center justify-center pb-6">
                {numberOfActiveEnrollments === 0 ? (
                  <div className="w-full sm:w-[80%] bg-white/10 rounded-md text-center py-10">
                    <p className="text-white text-sm">No active subjects!</p>
                    <p className="text-white text-xs mt-2 hover:underline">
                      <a href="/subjects">View All Subjects</a>
                    </p>
                  </div>
                ) : (
                  <div className="w-full flex items-center gap-3">
                    {/* Left arrow */}
                    {numberOfActiveEnrollments > 4 && (
                      <button
                        type="button"
                        className="hidden sm:block text-white shrink-0"
                        onClick={handleActivePrevious}
                        aria-label="Previous active subjects"
                      >
                        <BsArrowLeftCircle className="hover:text-[#1896b9] hover:opacity-30 transition w-[26px] h-[26px]" />
                      </button>
                    )}

                    {/* Responsive grid */}
                    <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {calculateActiveSubjects().map((subject, index) => (
                        <div
                          key={`${subject}-${index}`}
                          onClick={() => navigateToSubjectPage({ subject })}
                          className="bg-center bg-cover w-full h-[95px] sm:h-[110px] flex justify-center items-center hover:cursor-pointer rounded-md opacity-90 hover:opacity-100 transition-all duration-300"
                          style={{
                            backgroundImage: `url(${allSubjects[subject]})`,
                          }}
                        >
                          <p className="text-white shadow-md text-base sm:text-lg text-center px-2">
                            {subject}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Right arrow */}
                    {numberOfActiveEnrollments > 4 && (
                      <button
                        type="button"
                        className="hidden sm:block text-white shrink-0"
                        onClick={handleActiveNext}
                        aria-label="Next active subjects"
                      >
                        <BsArrowRightCircle className="hover:text-[#1896b9] hover:opacity-30 transition w-[26px] h-[26px]" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* ===================== PAST SUBJECTS ===================== */}
              <p className="text-white font-thin text-3xl sm:text-4xl 2xl:text-5xl py-4 sm:py-6">
                Past Subjects
              </p>

              <div className="flex items-center justify-center pb-4">
                {numberOfPastEnrollments === 0 ? (
                  <div className="w-full sm:w-[80%] bg-white/10 rounded-md text-center py-10">
                    <p className="text-white text-sm">No past subjects!</p>
                    <p className="text-white text-xs mt-2 hover:underline">
                      <a href="/subjects">View All Subjects</a>
                    </p>
                  </div>
                ) : (
                  <div className="w-full flex items-center gap-3">
                    {/* Left arrow */}
                    {numberOfPastEnrollments > 4 && (
                      <button
                        type="button"
                        className="hidden sm:block text-white shrink-0"
                        onClick={handlePastPrevious}
                        aria-label="Previous past subjects"
                      >
                        <BsArrowLeftCircle className="hover:text-[#1896b9] hover:opacity-30 transition w-[26px] h-[26px]" />
                      </button>
                    )}

                    {/* Responsive grid */}
                    <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {calculatePastSubjects().map((subject, index) => (
                        <div
                          key={`${subject}-${index}`}
                          onClick={() => navigate(`/${examSlug}/${slugify(subject)}`)}
                          className="bg-center bg-cover w-full h-[95px] sm:h-[110px] flex justify-center items-center hover:cursor-pointer rounded-md grayscale hover:grayscale-0 transition-all duration-300"
                          style={{
                            backgroundImage: `url(${allSubjects[subject]})`,
                          }}
                        >
                          <p className="text-white shadow-md text-base sm:text-lg text-center px-2">
                            {subject}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Right arrow */}
                    {numberOfPastEnrollments > 4 && (
                      <button
                        type="button"
                        className="hidden sm:block text-white shrink-0"
                        onClick={handlePastNext}
                        aria-label="Next past subjects"
                      >
                        <BsArrowRightCircle className="hover:text-[#1896b9] hover:opacity-30 transition w-[26px] h-[26px]" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </main>
          </div>
        </div>

        {/* Mobile sidebar drawer */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setSidebarOpen(false)}
            />

            <div className="absolute left-0 top-0 h-[100dvh] w-[85%] max-w-[320px] bg-[#0b1220] shadow-xl border-r border-white/10 overflow-hidden flex flex-col">
              <div className="p-4 flex items-center justify-between border-b border-white/10">
                <p className="text-white text-sm font-semibold">Menu</p>
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="rounded-md px-2 py-1 text-sm text-white/80 hover:bg-white/10"
                >
                  ✕
                </button>
              </div>

              <div className="p-2 overflow-y-auto flex-1">
                <SideNavigation />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;
